import { Routes, Route, Navigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

// Layout Components
import { AuthLayout, Layout } from '@/components/layout/Layout'

// Auth Pages
import { LoginPage } from '@/pages/auth/LoginPage'
import { RegisterPage } from '@/pages/auth/RegisterPage'
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage'

// Dashboard Pages
import { DashboardPage } from '@/pages/dashboard/DashboardPage'
import { MaterialsPage } from '@/pages/materials/MaterialsPage'
import { InventoryPage } from '@/pages/inventory/InventoryPage'
import { ProjectsPage } from '@/pages/projects/ProjectsPage'
import { TimeTrackingPage } from '@/pages/time-tracking/TimeTrackingPage'
import { ReportsPage } from '@/pages/reports/ReportsPage'
import { SettingsPage } from '@/pages/settings/SettingsPage'

// Products Pages
import { ProductsListPage } from '@/pages/products/ProductsListPage'
import { ProductViewPage } from '@/pages/products/ProductViewPage'
import { EditProductPage } from '@/pages/products/EditProductPage'
import { CreateProductFromProjectPage } from '@/pages/products/CreateProductFromProjectPage'
import { StockAdjustmentsReportPage } from '@/pages/reports/StockAdjustmentsReportPage'

// Template Pages
import { TemplateSelectionPage } from '@/pages/templates/TemplateSelectionPage'

// Hooks
import { useAuth } from '@/hooks/useAuth'

// Components
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { ErrorBoundary } from '@/components/ErrorBoundary'

function App() {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <Helmet>
        <title>Jewelry Inventory Tracker</title>
        <meta name="description" content="Professional jewelry inventory management system" />
      </Helmet>

      <Routes>
        {/* Public Routes */}
        <Route path="/auth" element={<AuthLayout />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route index element={<Navigate to="/auth/login" replace />} />
        </Route>

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            user ? <Layout /> : <Navigate to="/auth/login" replace />
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="materials/*" element={<MaterialsPage />} />
          <Route path="inventory/*" element={<InventoryPage />} />
          <Route path="projects/*" element={<ProjectsPage />} />
          <Route path="products" element={<ProductsListPage />} />
          <Route path="products/:id" element={<ProductViewPage />} />
          <Route path="products/:id/edit" element={<EditProductPage />} />
          <Route path="products/create-from-project" element={<CreateProductFromProjectPage />} />
          <Route path="products/create-from-project/:projectId" element={<CreateProductFromProjectPage />} />
          <Route path="time-tracking" element={<TimeTrackingPage />} />
          <Route path="reports/*" element={<ReportsPage />} />
          <Route path="settings/*" element={<SettingsPage />} />
          <Route path="reports/stock-adjustments" element={<StockAdjustmentsReportPage />} />
          <Route path="templates/select" element={<TemplateSelectionPage />} />
          <Route path="templates/switch" element={<TemplateSelectionPage mode="switch" />} />
        </Route>

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </ErrorBoundary>
  )
}

export default App